/**
 * CameraService — Capacitor camera + browser file input fallback.
 */
import { isNative } from '../utils/platform';

function getCameraPlugin() {
  return window.Capacitor?.Plugins?.Camera || null;
}

export class CameraService {
  static async takePhoto(type = 'general') {
    try {
      const cam = getCameraPlugin();
      if (cam) {
        const photo = await cam.getPhoto({
          quality: 85, allowEditing: false, resultType: 'base64',
          source: 'CAMERA', direction: 'REAR', correctOrientation: true,
        });
        return this._mapPhoto(photo);
      }
      return await this._browserFile('environment');
    } catch (e) {
      if (e?.message?.includes('cancel')) return null;
      console.error('[Camera] takePhoto error:', e);
      return null;
    }
  }

  static async pickFromGallery() {
    try {
      const cam = getCameraPlugin();
      if (cam) {
        const photo = await cam.getPhoto({
          quality: 85, allowEditing: false, resultType: 'base64', source: 'PHOTOS',
        });
        return this._mapPhoto(photo);
      }
      return await this._browserFile();
    } catch (e) {
      if (e?.message?.includes('cancel')) return null;
      return null;
    }
  }

  static async uploadProgressPhoto(photo, type, authToken) {
    try {
      const byteChars = atob(photo.base64);
      const byteArray = new Uint8Array(Array.from(byteChars).map(c => c.charCodeAt(0)));
      const blob = new Blob([byteArray], { type: photo.mimeType });
      const formData = new FormData();
      formData.append('photo', blob, `progress-${type}-${Date.now()}.jpg`);
      formData.append('type', type);
      const res = await fetch('/api/progress-photos', {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}` }, body: formData,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      return await res.json();
    } catch (e) {
      console.error('[Camera] Upload error:', e);
      return null;
    }
  }

  static _mapPhoto(photo) {
    if (!photo.base64String) return null;
    const mime = photo.format === 'png' ? 'image/png' : 'image/jpeg';
    return { base64: photo.base64String, mimeType: mime, dataUrl: `data:${mime};base64,${photo.base64String}` };
  }

  static _browserFile(capture) {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      if (capture) input.capture = capture;
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (!file) { resolve(null); return; }
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const base64 = dataUrl.split(',')[1];
          resolve({ base64, mimeType: file.type, dataUrl });
        };
        reader.readAsDataURL(file);
      };
      input.click();
    });
  }
}
